/**
 * Stacked part-of-whole splits, in either orientation.
 *
 * `orientation: 'row'` (default) — one horizontal bar per row, geometry ported from
 * the right pane of block ② of docs/plans/conversations/09-dashboard-mockup.html.
 *
 * `orientation: 'column'` — the BP mock's `.vchart`: a shared percentage axis on the
 * left and one 64px column per bucket. Use it when the buckets are a short TIME
 * series (two measured days, three months): columns put time on the x-axis, where a
 * reader already expects it, and the % axis makes two unequal-n buckets comparable.
 * The row form is for a handful of named categories, where the label needs the room.
 *
 * Shared rules:
 *  - `value` is a SHARE of the row total (the caller divides once; never an average
 *    of rates).
 *  - 2px gap in the surface colour between touching segments; only the LAST segment
 *    gets the rounded data end, interior ones stay square.
 *  - An inline segment label is drawn only when the measured text FITS with padding
 *    on both sides. Too narrow: the column form moves it outside next to the
 *    segment, the row form leaves it to the legend + tooltip. Never clipped.
 *  - Legend always present (>= 2 series by construction).
 *  - Parts stack in ARRAY ORDER from the bottom up in column mode (so a
 *    [positive, neutral, negative] call puts positive at the base, as the mock
 *    does), and left to right in row mode.
 */

import { Box } from '@mui/material';
import { VIZ, fmtInt, fmtPct, hBarPath, inkOn, measureText, shade, useMeasuredWidth } from './tokens';
import { EmptyNote, VizLegend } from './ChartCard';
import { useVizTooltip } from './useVizTooltip';

export interface StackedBarsRow {
  label: string;
  n?: number;
  parts: { key: string; label: string; value: number; color: string }[];
}

export interface StackedBarsProps {
  rows: StackedBarsRow[];
  format?: (v: number) => string;
  emptyNote?: string;
  orientation?: 'row' | 'column';
  /** Column mode: what `n` counts, e.g. « 60 réponses ». `n` alone is ambiguous. */
  nSuffix?: string;
}

const BAR_H = 22;
const GAP_X = 2; // surface-colour gap between segments
const ROW_H = 46;
const PAD = 6; // minimum padding on both sides of an inline label
const COL_H = 180; // column-mode plot height (mock `.vplot`)
const COL_W = 64;
/** Below this share an inline label cannot clear the segment edges. */
const COL_INLINE_MIN = 0.1;

export default function StackedBars({
  rows,
  format = fmtPct,
  emptyNote = 'Aucune donnée sur cette sélection.',
  orientation = 'row',
  nSuffix,
}: StackedBarsProps) {
  const [ref, width] = useMeasuredWidth<HTMLDivElement>(640);
  const tip = useVizTooltip();

  if (!rows.length || !rows[0].parts.length) return <EmptyNote>{emptyNote}</EmptyNote>;

  if (orientation === 'column') {
    return (
      <Box ref={ref} sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          {/* The axis is the point of this form: two buckets with different `n` are
              only comparable as shares, so the scale is always 0-100 %. */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              height: COL_H,
              width: 40,
              flex: 'none',
              fontSize: 12,
              color: VIZ.muted,
              textAlign: 'right',
            }}
          >
            <span>{format(1)}</span>
            <span>{format(0.5)}</span>
            <span>{format(0)}</span>
          </Box>
          <Box sx={{ position: 'relative', flex: 1, height: COL_H }}>
            {/* A rule for VALUE t sits at `1 - t` from the top, so 0 is the bottom —
                and the bottom one is the BASELINE, in the darker axis ink. */}
            {[1, 0.5, 0].map((t) => (
              <Box
                key={t}
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: `${(1 - t) * 100}%`,
                  borderTop: `1px solid ${t === 0 ? VIZ.axis : VIZ.grid}`,
                }}
              />
            ))}
            {/* ONE grid, and the label row below uses the SAME template — two
                `space-around` flex rows with fixed-width children drift apart as
                soon as they overflow, which put a column under the wrong date
                (2026-10-08). Columns flex and are capped, never fixed. */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'grid',
                gridTemplateColumns: `repeat(${rows.length}, minmax(0, 1fr))`,
                alignItems: 'stretch',
                justifyItems: 'center',
              }}
            >
              {rows.map((row, ri) => {
                const total = row.parts.reduce((s, p) => s + p.value, 0) || 1;
                let acc = 0;
                const placed = row.parts.map((p) => {
                  const share = p.value / total;
                  const mid = acc + share / 2;
                  acc += share;
                  return { ...p, share, mid };
                });
                // The outside label flips to the LEFT on the last column, where
                // `left: 100% + 8px` would push it off the card.
                const outsideLeft = ri === rows.length - 1 && rows.length > 1;
                return (
                  <Box key={row.label} sx={{ position: 'relative', width: '100%', maxWidth: COL_W }}>
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column-reverse',
                        gap: '2px',
                        borderRadius: '10px 10px 4px 4px',
                        overflow: 'hidden',
                      }}
                    >
                      {placed
                        .filter((p) => p.share > 0)
                        .map((p) => (
                          <Box
                            key={p.key}
                            sx={{
                              height: `${p.share * 100}%`,
                              bgcolor: p.color,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 12,
                              fontWeight: 600,
                              color: inkOn(p.color),
                              cursor: 'pointer',
                            }}
                            onPointerMove={(ev) =>
                              tip.show(ev, [`${row.label} · ${p.label}`, format(p.value)])
                            }
                            onPointerLeave={tip.hide}
                          >
                            {p.share >= COL_INLINE_MIN ? format(p.value) : ''}
                          </Box>
                        ))}
                    </Box>
                    {/* A sliver carries its value OUTSIDE rather than losing it: an
                        8 % positive share is exactly the number the reader came for. */}
                    {placed
                      .filter((p) => p.share > 0 && p.share < COL_INLINE_MIN)
                      .map((p) => (
                        <Box
                          component="span"
                          key={`out-${p.key}`}
                          sx={{
                            position: 'absolute',
                            ...(outsideLeft
                              ? { right: 'calc(100% + 8px)' }
                              : { left: 'calc(100% + 8px)' }),
                            bottom: `${p.mid * 100}%`,
                            transform: 'translateY(50%)',
                            fontSize: 12,
                            fontWeight: 600,
                            color: shade(p.color, 0.3),
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {format(p.value)}
                        </Box>
                      ))}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${rows.length}, minmax(0, 1fr))`,
            justifyItems: 'center',
            // 40px axis column + the 12px flex gap above it.
            ml: '52px',
            mt: 1,
          }}
        >
          {rows.map((row) => (
            <Box
              key={row.label}
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                px: 0.5,
                minWidth: 0,
                // Crowded axes ELLIPSIS rather than overlapping: two dates printed on
                // top of each other is worse than one that is visibly cut.
                '& > *': { maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
              }}
            >
              <Box component="b" sx={{ fontSize: 13, fontWeight: 500, color: VIZ.ink }}>
                {row.label}
              </Box>
              {row.n != null && (
                <Box component="span" sx={{ fontSize: 12, color: VIZ.muted }}>
                  {fmtInt(row.n)}
                  {nSuffix ? ` ${nSuffix}` : ''}
                </Box>
              )}
            </Box>
          ))}
        </Box>
        <VizLegend items={rows[0].parts.map((p) => ({ label: p.label, color: p.color }))} />
        {tip.node}
      </Box>
    );
  }

  const labelW = Math.min(220, Math.max(80, ...rows.map((r) => measureText(r.label, 12) + 14)));
  const m = { l: labelW, r: 8, t: 6 };
  const W = Math.max(320, width);
  const H = rows.length * ROW_H + 10;
  const pw = Math.max(40, W - m.l - m.r);

  return (
    <Box ref={ref} sx={{ width: '100%' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        style={{ display: 'block', overflow: 'visible' }}
        role="img"
        aria-label={`Répartition ${rows[0].parts.map((p) => p.label).join(' / ')} par ligne`}
      >
        {rows.map((row, i) => {
          const y = m.t + i * ROW_H + 4;
          const total = row.parts.reduce((s, p) => s + p.value, 0) || 1;
          let x = m.l;
          return (
            <g key={row.label}>
              <text x={m.l - 10} y={y + BAR_H - 6} textAnchor="end" fill={VIZ.ink2} fontSize={12}>
                {row.label}
              </text>
              {row.n != null && (
                <text
                  x={m.l - 10}
                  y={y + BAR_H + 9}
                  textAnchor="end"
                  fill={VIZ.muted}
                  fontSize={10.5}
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {fmtInt(row.n)}
                </text>
              )}
              {row.parts.map((p, j) => {
                const last = j === row.parts.length - 1;
                const w = Math.max(0, (p.value / total) * pw - (last ? 0 : GAP_X));
                const sx = x;
                x += w + GAP_X;
                const text = format(p.value);
                const fits = measureText(text, 11.5, 600) + 2 * PAD <= w;
                return (
                  <g key={p.key}>
                    <path
                      d={last ? hBarPath(sx, y, w, BAR_H, 4) : `M${sx},${y}h${w}v${BAR_H}h${-w}z`}
                      fill={p.color}
                      style={{ cursor: 'pointer' }}
                      onPointerMove={(ev) =>
                        tip.show(ev, [`${row.label} · ${p.label}`, format(p.value)])
                      }
                      onPointerLeave={tip.hide}
                    />
                    {fits && (
                      <text
                        x={sx + w / 2}
                        y={y + BAR_H / 2 + 4}
                        textAnchor="middle"
                        fill={inkOn(p.color)}
                        fontSize={11.5}
                        fontWeight={600}
                        style={{ pointerEvents: 'none', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {text}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
      <VizLegend items={rows[0].parts.map((p) => ({ label: p.label, color: p.color }))} />
      {tip.node}
    </Box>
  );
}
